import { Fragment, useCallback, useRef, useState } from "react";
import { Box } from "@mui/material";
import { CollapsibleItem } from "../../../../components/shared/sidebar/CollapsibleItem";
import { useSelectionContext } from "../../../../context/SelectionContext";
import { useDateSearchParamOrFallbackToToday } from "../../../../hooks/useDateSearchParamOrFallbackToToday";
import { useSelectedServiceCenter } from "../../../../hooks/useSelectedServiceCenter";
import { dateToInt } from "../../../../utils/dateTimeHelper";
import { isSelectedDateTodayOrInTheFutureForCurrentSic } from "../../../../hooks/useIsSelectedDateTodayOrInTheFutureForCurrentSic";
import { useGetUnplannedTrapsSummary } from "../../../../hooks/react-query/preplanning/useTraps";
import { tableSectionToIconMap } from "../TableActionBar";
import Loading from "../../../../components/shared/layout/Loading";
import {
  useActionsMenuContext,
  type SummaryContext,
} from "../ActionsMenuContext";
import type { ContextMenuRef } from "../../../../components/shared/ContextMenuDropdown";
import ContextMenuDropdown from "../../../../components/shared/ContextMenuDropdown";
import { unifyShipmentContext } from "../../../../utils/unifyShipmentContext";
import { contextMenuActions } from "../../prePlanning/tables/contextMenuActions";
import { useValidateMergeRequest } from "../../../../hooks/useValidateMergeRequest";
import PrePlanningDroppableSectionItem from "./PrePlanningDroppableSectionItem";

export const Traps = () => {
  const contextMenuRef = useRef<ContextMenuRef<SummaryContext>>(null);
  const { openDialog } = useActionsMenuContext();
  const { selected, isAllSelected, clearSelection } = useSelectionContext();
  const [selectedDate] = useDateSearchParamOrFallbackToToday();
  const planDate = dateToInt(selectedDate);
  const [serviceCenter] = useSelectedServiceCenter();

  const [isExpanded, setIsExpanded] = useState(false);

  const { data: traps, isLoading } = useGetUnplannedTrapsSummary(
    serviceCenter.id,
    planDate
  );

  const isEditingAllowed = isSelectedDateTodayOrInTheFutureForCurrentSic(
    selectedDate,
    serviceCenter
  );

  const selectionToContext = useCallback(
    (item?: SummaryContext): SummaryContext[] => {
      if (item) {
        return [
          {
            id: String(item.id),
            shipmentIds: item.shipmentIds,
            type: "trap",
          },
        ];
      }
      return unifyShipmentContext(
        selected.map(({ id }) => {
          const trap = traps?.find((trap) => String(trap.id) === String(id));
          return {
            id,
            shipmentIds: trap ? trap.shipmentIds : [],
            type: "trap",
          };
        })
      );
    },
    [selected, traps]
  );

  const { validateMergeRequest } = useValidateMergeRequest(
    selectionToContext(),
    traps || []
  );

  const contextActions = useCallback(
    (item?: SummaryContext) =>
      contextMenuActions({
        section: "traps",
        selectedTrapOrBucket: traps?.filter((trap) =>
          selectionToContext().find(
            (context) => String(context.id) === String(trap.id)
          )
        ),
        shipmentContext:
          selected.length > 1 ? selectionToContext() : selectionToContext(item),
        openDialog,
        clearSelection,
        validateMergeRequest,
      }),
    [
      clearSelection,
      openDialog,
      selected.length,
      selectionToContext,
      traps,
      validateMergeRequest,
    ]
  );

  if (isLoading) {
    return <Loading size={32} label="sidebar traps" />;
  }

  // If traps is undefined, since it's the entire section, so do not render it.
  if (!traps) {
    return;
  }

  return (
    <Box>
      <CollapsibleItem
        title="Traps"
        displayCount={traps.length}
        icon={tableSectionToIconMap["trap"]}
        isExpanded={isExpanded}
        onExpand={(value: boolean) => setIsExpanded(value)}
      >
        {traps.map((trap, index) => {
          const { id, shipmentIds } = trap;
          return (
            <Fragment key={id}>
              <PrePlanningDroppableSectionItem
                index={index}
                data={trap}
                isEditingAllowed={isEditingAllowed}
                isSelected={isAllSelected(shipmentIds, "trap")}
                selectedDate={selectedDate}
                inboundPage="pre-planning"
                tab="traps"
                icon="trap"
                contextRef={contextMenuRef}
              />
            </Fragment>
          );
        })}
      </CollapsibleItem>
      <ContextMenuDropdown actions={contextActions} ref={contextMenuRef} />
    </Box>
  );
};
